import { type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface ModernColumn {
  /** Stable key. */
  key: string;
  /** Header label. */
  header: ReactNode;
  /** Optional fixed/preferred width, e.g. "12rem" or "30%". */
  width?: string;
  /** Right-align numeric columns. */
  align?: 'left' | 'right' | 'center';
  /** Hide on small screens. */
  hideOnMobile?: boolean;
}

/**
 * ModernDataTableShell — the chrome for a clean, low-clutter data table:
 * a card surface, an optional toolbar (search / filters / actions), a quiet
 * header row, and a scroll body. It renders the structure only — rows are
 * provided as children (use ModernTableRow), and column metadata drives the
 * header and the grid template. Data + sorting + selection come from the
 * caller (the existing app hooks), not from here.
 */
export function ModernDataTableShell({
  title,
  columns,
  toolbar,
  footer,
  children,
  selectable = false,
  empty,
  isEmpty = false,
  className,
}: {
  title?: ReactNode;
  columns: ModernColumn[];
  toolbar?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  selectable?: boolean;
  empty?: ReactNode;
  isEmpty?: boolean;
  className?: string;
}) {
  const gridTemplate = buildGridTemplate(columns, selectable);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--mui-shadow-card)]',
        className,
      )}
    >
      {(title || toolbar) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
          {toolbar && <div className="flex flex-1 items-center justify-end gap-2">{toolbar}</div>}
        </div>
      )}

      {/* Header row */}
      <div
        role="row"
        className="grid items-center gap-3 border-b border-border bg-muted/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {selectable && <span aria-hidden className="h-3.5 w-3.5" />}
        {columns.map((col) => (
          <span
            key={col.key}
            className={cn(
              'truncate',
              col.align === 'right' && 'text-right',
              col.align === 'center' && 'text-center',
              col.hideOnMobile && 'hidden sm:block',
            )}
          >
            {col.header}
          </span>
        ))}
      </div>

      {/* Body */}
      <div className="divide-y divide-border">
        {isEmpty
          ? empty ?? (
              <div className="px-4 py-12 text-center text-[13px] text-muted-foreground">
                Nothing to show yet.
              </div>
            )
          : children}
      </div>

      {footer && (
        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-4 py-2.5 text-[12px] text-muted-foreground">
          {footer}
        </div>
      )}
    </div>
  );
}

export function buildGridTemplate(columns: ModernColumn[], selectable: boolean): string {
  const cols = columns.map((c) => c.width ?? 'minmax(0, 1fr)');
  return selectable ? `1.25rem ${cols.join(' ')}` : cols.join(' ');
}
